// src/pages/materias/laboral/components/DatosExpedienteCard.jsx
import React from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
} from "antd";
import { HappyProvider } from "@ant-design/happy-work-theme";
import ScrollDatePicker from "../../calendar/ScrollDatePicker";

import { fmtDate } from "./laboralExpedienteFormatters";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

// Campo simple "label arriba / valor abajo", sin bordes de tabla — reemplaza
// el look de tabla zebrada de antd <Descriptions bordered> por tarjetas más
// aireadas (mismo patrón de organización que EventoDetallePage de HerrSoft).
function Field({ label, value, span2 = false }) {
  return (
    <div className={`laboral-exp-field${span2 ? " laboral-exp-field--span2" : ""}`}>
      <span className="laboral-exp-field-label">{label}</span>
      <span className="laboral-exp-field-value">{value ?? "—"}</span>
    </div>
  );
}

export default function DatosExpedienteCard({
  detalle,
  ObjetoName,
  estadoName,
  ciudadName,
  ambitoName,
  proximaAudiencia,
  onExportPDF,
  exportingPDF = false,
  audiencias,
  onAdelantarAudiencia,
  canEditar = true,

  // Edición inline — tarjeta "Datos del expediente"
  editingDatosCard = false,
  onStartEditDatos,
  onCancelEditDatos,
  onSaveDatos,
  savingDatosCard = false,
  editingDatosForm = {},
  setEditingDatosForm,
  estadosOptionsFinal = [],
  ciudadesOptionsFinal = [],
  autoridadOptions = [],
  objetosOptions = [],
  medioNotifOptions = [],
  selectedEstadoId,
  setSelectedEstadoId,
  selectedCiudadId,
  setSelectedCiudadId,
  selectedAutoridadId,
  setSelectedAutoridadId,

  // Edición inline — tarjeta "Solicitantes"
  editingTrabajadoresCard = false,
  onStartEditTrabajadores,
  onCancelEditTrabajadores,
  onSaveTrabajadores,
  savingTrabajadoresCard = false,
  editingTrabajadoresForm = {},
  setEditingTrabajadoresForm,

  // Edición inline — tarjeta "Razones Sociales"
  editingRazonesCard = false,
  onStartEditRazones,
  onCancelEditRazones,
  onSaveRazones,
  savingRazonesCard = false,
  editingRazonesForm = {},
  setEditingRazonesForm,
  razonesSocialesOptions = [],
}) {

  const navigate = useNavigate();

  const toTitleCase = (str) => {
    if (!str || typeof str !== "string") return "—";
    return str
      .toLowerCase()
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const formatNombrePersona = (str) => {
    if (!str || typeof str !== "string") return "—";
    return str
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .split(" ")
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(" ");
  };

  const formaNotificacion = detalle.tipo_notificado
    ? detalle.tipo_notificado === "actuario"
      ? "Notificador CCL"
      : "Solicitante"
    : "—";

  const aQuienSeNotifico = detalle.tipo_notificado_actuario
    ? detalle.tipo_notificado_actuario.charAt(0).toUpperCase() +
      detalle.tipo_notificado_actuario.slice(1)
    : "—";

  const fechaRecepcionCitatorio = detalle.fecha_notificacion
    ? fmtDate(detalle.fecha_notificacion)
    : "—";

  const razonesSociales =
    Array.isArray(detalle.razones_sociales) && detalle.razones_sociales.length > 0
      ? detalle.razones_sociales
      : [];

  const trabajadoresList =
    Array.isArray(detalle.trabajadores) && detalle.trabajadores.length > 0
      ? detalle.trabajadores
      : [];

  // =========================
  // ESTATUS (id_conciliacion_status)
  // =========================
  const STATUS_ACTIVO = 1;
  const STATUS_CONVENIO = 2;
  const STATUS_DIFERIMIENTO = 3;
  const STATUS_ARCHIVO_INCOMP_SOLICITANTE = 4;
  const STATUS_ARCHIVO_INCOMP_PATRON = 5;
  const STATUS_NO_CONCILIACION = 6;

  const statusId = Number(detalle?.id_conciliacion_status ?? 0);

  const esActivo = statusId === STATUS_ACTIVO;
  const esDiferimiento = statusId === STATUS_DIFERIMIENTO;
  const esConvenio = statusId === STATUS_CONVENIO;
  const esNoConciliacion = statusId === STATUS_NO_CONCILIACION;
  const esArchivoSolicitante = statusId === STATUS_ARCHIVO_INCOMP_SOLICITANTE;
  const esArchivoPatron = statusId === STATUS_ARCHIVO_INCOMP_PATRON;

  // =========================
  // TOMAR "AUDIENCIA" (desde audiencias)
  // - tu JSON viene como { expediente, count, items:[...] }
  // =========================
  const audienciaActual =
    (audiencias && Array.isArray(audiencias.items) && audiencias.items[0]) ||
    (Array.isArray(audiencias) && audiencias[0]) ||
    null;

  // Convenio: decidir si ya hay constancia (cumplido) o si toca mostrar fecha de pago
  const isConstanciaDocumento = Number(audienciaActual?.is_constancia_documento ?? 0) === 1;

  // Fecha de pago (solo si es convenio y NO hay constancia)
  const fechaPagoConvenioRaw =
    audienciaActual?.convenio?.[0]?.fecha_pago ||
    audienciaActual?.pagos_convenio?.[0]?.fecha ||
    null;

  const fechaPagoConvenio = fechaPagoConvenioRaw ? fmtDate(fechaPagoConvenioRaw) : "—";

  // =========================
  // PRÓXIMA AUDIENCIA (solo cuando aplica)
  // =========================
  const proximaAudienciaText =
    proximaAudiencia ||
    (detalle.fecha_proxima_audiencia ? fmtDate(detalle.fecha_proxima_audiencia) : "Pendiente de programar");

  // =========================
  // MENSAJE DEL CUADRO AZUL
  // =========================
  // =========================
// MENSAJE DEL CUADRO AZUL
// =========================
let blueHeader = "Estatus del expediente";
let blueBody = "—";
let blueSub = null; // <- NUEVO: línea corta descriptiva

if (esActivo || esDiferimiento) {
  blueHeader = "Próxima audiencia";
  blueBody = proximaAudienciaText;
  blueSub = null;
} else if (esConvenio) {
  if (isConstanciaDocumento) {
    blueHeader = "Estatus del expediente";
    blueBody = "Caso concluido por convenio (cumplido)";
    blueSub = null;
  } else {
    blueHeader = "Próximo pago";
    blueBody = fechaPagoConvenio !== "—" ? fechaPagoConvenio : "Pendiente de definir";
    blueSub = "Convenio pendiente de constancia";
  }
} else if (esNoConciliacion) {
  blueHeader = "Estatus del expediente";
  blueBody = "Caso concluido por no conciliación";
  blueSub = null;
} else if (esArchivoSolicitante) {
  blueHeader = "Estatus del expediente";
  blueBody = "Archivo por incomparecencia del solicitante";
  blueSub = null;
} else if (esArchivoPatron) {
  blueHeader = "Estatus del expediente";
  blueBody = "Archivo por incomparecencia del patrón";
  blueSub = null;
} else if (isConstanciaDocumento) {
  blueHeader = "Estatus del expediente";
  blueBody = "Caso concluido por constancia de cumplimiento";
  blueSub = null;
}


  // Botón Convenio Anticipado: SOLO Activo o Diferimiento
  const showConvenioAnticipadoBtn = esActivo || esDiferimiento;

  return (
    <div>
      {/* CARD 1: DATOS DEL EXPEDIENTE + CUADRO AZUL (cuadro azul fuera de la card blanca) */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={20}>
          <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ height: "100%" }}>
            <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="laboral-exp-icon-badge">
                  <i className="fa-solid fa-file" />
                </div>
                <h3 className="laboral-exp-card-header-title">Datos del expediente</h3>
              </div>

              {canEditar && !editingDatosCard && (
                <Button
                  onClick={onStartEditDatos}
                  className="laboral-exp-btn-clean"
                >
                  Editar
                </Button>
              )}
            </div>

            {editingDatosCard ? (
              <div className="laboral-exp-edit-form">
                <div className="laboral-exp-edit-grid">
                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Estado</span>
                    <Select
                      style={{ width: "100%" }}
                      showSearch
                      optionFilterProp="label"
                      placeholder="Selecciona un estado"
                      options={estadosOptionsFinal}
                      value={selectedEstadoId}
                      onChange={(v) => {
                        setSelectedEstadoId(v ?? null);
                        setSelectedCiudadId(null);
                        setSelectedAutoridadId(null);
                      }}
                    />
                  </div>

                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Ciudad</span>
                    <Select
                      style={{ width: "100%" }}
                      showSearch
                      optionFilterProp="label"
                      disabled={!selectedEstadoId}
                      placeholder={!selectedEstadoId ? "Selecciona un estado primero" : "Selecciona una ciudad"}
                      options={ciudadesOptionsFinal}
                      value={selectedCiudadId}
                      onChange={(v) => {
                        setSelectedCiudadId(v ?? null);
                        setSelectedAutoridadId(null);
                      }}
                    />
                  </div>

                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Autoridad</span>
                    <Select
                      style={{ width: "100%" }}
                      showSearch
                      optionFilterProp="label"
                      disabled={!selectedCiudadId}
                      placeholder={!selectedCiudadId ? "Selecciona una ciudad primero" : "Selecciona una autoridad"}
                      options={autoridadOptions}
                      value={selectedAutoridadId}
                      onChange={(v) => setSelectedAutoridadId(v ?? null)}
                    />
                  </div>

                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Fecha de emisión del citatorio</span>
                    <ScrollDatePicker
                      style={{ width: "100%" }}
                      value={editingDatosForm.fecha_emision_expediente}
                      onChange={(v) => setEditingDatosForm((f) => ({ ...f, fecha_emision_expediente: v }))}
                      format="DD/MM/YYYY"
                    />
                  </div>

                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Objeto</span>
                    <Select
                      style={{ width: "100%" }}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Selecciona el objeto"
                      options={objetosOptions}
                      value={editingDatosForm.id_objeto}
                      onChange={(v) => setEditingDatosForm((f) => ({ ...f, id_objeto: v }))}
                    />
                  </div>

                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Núm. ID Único</span>
                    <Input
                      value={editingDatosForm.expediente}
                      onChange={(e) => setEditingDatosForm((f) => ({ ...f, expediente: e.target.value }))}
                    />
                  </div>

                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Fecha y hora de la próxima audiencia</span>
                    <ScrollDatePicker
                      style={{ width: "100%" }}
                      showTime={{ format: "HH:mm" }}
                      format="DD/MM/YYYY HH:mm"
                      value={editingDatosForm.fecha_audiencia}
                      onChange={(v) => setEditingDatosForm((f) => ({ ...f, fecha_audiencia: v }))}
                    />
                  </div>

                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Forma de notificación</span>
                    <Select
                      style={{ width: "100%" }}
                      allowClear
                      placeholder="Selecciona"
                      options={[
                        { label: "Solicitante", value: "trabajador" },
                        { label: "Notificador CCL", value: "actuario" },
                      ]}
                      value={editingDatosForm.tipo_notificado}
                      onChange={(v) =>
                        setEditingDatosForm((f) => ({
                          ...f,
                          tipo_notificado: v ?? null,
                          tipo_notificado_actuario: v ? f.tipo_notificado_actuario : null,
                          fecha_notificacion: v === "actuario" ? f.fecha_notificacion : null,
                          id_medio_notificacion: v === "trabajador" ? f.id_medio_notificacion : null,
                        }))
                      }
                    />
                  </div>

                  {editingDatosForm.tipo_notificado && (
                    <div className="laboral-exp-edit-field">
                      <span className="laboral-exp-edit-label">A quién se le notificó</span>
                      <Select
                        style={{ width: "100%" }}
                        allowClear
                        placeholder="Selecciona"
                        options={[
                          { label: "Empresa", value: "empresa" },
                          { label: "Despacho", value: "despacho" },
                        ]}
                        value={editingDatosForm.tipo_notificado_actuario}
                        onChange={(v) => setEditingDatosForm((f) => ({ ...f, tipo_notificado_actuario: v ?? null }))}
                      />
                    </div>
                  )}

                  {editingDatosForm.tipo_notificado === "actuario" && (
                    <div className="laboral-exp-edit-field">
                      <span className="laboral-exp-edit-label">Fecha y hora de recepción del citatorio</span>
                      <ScrollDatePicker
                        style={{ width: "100%" }}
                        showTime={{ format: "HH:mm" }}
                        format="DD/MM/YYYY HH:mm"
                        value={editingDatosForm.fecha_notificacion}
                        onChange={(v) => setEditingDatosForm((f) => ({ ...f, fecha_notificacion: v }))}
                      />
                    </div>
                  )}

                  {editingDatosForm.tipo_notificado === "trabajador" && (
                    <div className="laboral-exp-edit-field">
                      <span className="laboral-exp-edit-label">Medio notificado</span>
                      <Select
                        style={{ width: "100%" }}
                        allowClear
                        placeholder="Selecciona medio"
                        options={medioNotifOptions}
                        value={editingDatosForm.id_medio_notificacion}
                        onChange={(v) => setEditingDatosForm((f) => ({ ...f, id_medio_notificacion: v ?? null }))}
                      />
                    </div>
                  )}
                </div>

                <div className="laboral-exp-edit-actions">
                  <Button onClick={onCancelEditDatos} className="laboral-exp-btn-clean">
                    Cancelar
                  </Button>
                  <HappyProvider>
                    <Button
                      type="primary"
                      loading={savingDatosCard}
                      onClick={onSaveDatos}
                      className="laboral-exp-btn-save"
                      icon={<i className="fa-solid fa-floppy-disk" />}
                    >
                      Guardar
                    </Button>
                  </HappyProvider>
                </div>
              </div>
            ) : (
              <div className="laboral-exp-field-grid">
                <Field label="Núm. ID Único" value={detalle.expediente || "—"} />
                <Field
                  label="Fecha de emisión del citatorio"
                  value={fmtDate(detalle.fecha_emision_expediente || detalle.created_at)}
                />
                <Field label="Objeto" value={toTitleCase(ObjetoName)} />
                <Field label="Estado" value={toTitleCase(estadoName)} />
                <Field label="Ciudad" value={toTitleCase(ciudadName)} />
                <Field label="Competencia" value={toTitleCase(ambitoName)} />
                <Field
                  label="Autoridad"
                  value={
                    toTitleCase(detalle.nombre_autoridad) ||
                    toTitleCase(detalle.autoridad_nombre) ||
                    toTitleCase(detalle.autoridad) ||
                    "—"
                  }
                />
                <Field label="Forma de notificación" value={formaNotificacion} />

                {detalle.tipo_notificado && (
                  <Field label="A quién se le notificó" value={aQuienSeNotifico} />
                )}

                {detalle.tipo_notificado === "actuario" ? (
                  <Field
                    label="Fecha y hora de recepción del citatorio"
                    value={fechaRecepcionCitatorio}
                  />
                ) : detalle.tipo_notificado === "trabajador" ? (
                  <Field label="Medio notificado" value={toTitleCase(detalle.medio_notificacion)} />
                ) : null}
              </div>
            )}
          </Card>
        </Col>

        {/* CUADRO AZUL — fuera de la card blanca de "Datos del expediente" */}
        <Col xs={24} lg={4}>
          <div className="laboral-exp-next-aud-card">
            <div className="laboral-exp-next-aud-header">
              <span className="laboral-exp-chip-label">
                {blueHeader}
              </span>
            </div>
            <div className="laboral-exp-next-aud-body">
              <Text className="laboral-exp-next-aud-date">{blueBody}</Text>
              {blueSub ? (
                <Text className="laboral-exp-next-aud-sub" style={{ display: "block", marginTop: 6 }}>
                  {blueSub}
                </Text>
              ) : null}
            </div>


            <div className="laboral-exp-next-aud-footer">
              {showConvenioAnticipadoBtn ? (
                <Button
                  type="primary"
                  size="large"
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    padding: "10px 26px",
                    borderRadius: "10px",
                  }}
                  onClick={() => onAdelantarAudiencia?.()}
                >
                  Convenio Anticipado
                </Button>
              ) : null}
            </div>
          </div>
        </Col>
      </Row>

      <div style={{ height: 16 }} />

      {/* CARD 2: SOLICITANTES + RAZONES SOCIALES — cada una con su propia edición, sin mover a la otra */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={8}>
          <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ height: "100%" }}>
            {editingTrabajadoresCard ? (
              <>
                <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="laboral-exp-icon-badge">
                      <i className="fas fa-users" />
                    </div>
                    <h3 className="laboral-exp-card-header-title">Solicitantes</h3>
                  </div>
                  <span className="laboral-exp-edit-badge">
                    {(editingTrabajadoresForm.trabajadores || [""]).length}
                  </span>
                </div>

                <div className="laboral-exp-edit-trabajadores-list">
                  {(editingTrabajadoresForm.trabajadores || [""]).map((nombre, idx) => (
                    <div key={idx} className="laboral-exp-edit-trabajador-row">
                      <Input
                        value={nombre}
                        placeholder={`Trabajador ${idx + 1} (nombre completo)`}
                        onChange={(e) => {
                          const next = [...(editingTrabajadoresForm.trabajadores || [""])];
                          next[idx] = e.target.value;
                          setEditingTrabajadoresForm((f) => ({ ...f, trabajadores: next }));
                        }}
                      />
                      <Button
                        disabled={(editingTrabajadoresForm.trabajadores || []).length <= 1}
                        onClick={() => {
                          const next = (editingTrabajadoresForm.trabajadores || []).filter((_, i) => i !== idx);
                          setEditingTrabajadoresForm((f) => ({ ...f, trabajadores: next }));
                        }}
                      >
                        Quitar
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="dashed"
                  block
                  icon={<i className="fa-solid fa-plus" />}
                  style={{ marginTop: 4 }}
                  onClick={() =>
                    setEditingTrabajadoresForm((f) => ({
                      ...f,
                      trabajadores: [...(f.trabajadores || [""]), ""],
                    }))
                  }
                >
                  Agregar trabajador
                </Button>

                <div className="laboral-exp-edit-actions">
                  <Button onClick={onCancelEditTrabajadores} className="laboral-exp-btn-clean">
                    Cancelar
                  </Button>
                  <HappyProvider>
                    <Button
                      type="primary"
                      loading={savingTrabajadoresCard}
                      onClick={onSaveTrabajadores}
                      className="laboral-exp-btn-save"
                      icon={<i className="fa-solid fa-floppy-disk" />}
                    >
                      Guardar
                    </Button>
                  </HappyProvider>
                </div>
              </>
            ) : (
              <>
                <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="laboral-exp-icon-badge">
                      <i className="fas fa-users" />
                    </div>
                    <h3 className="laboral-exp-card-header-title">Solicitantes</h3>
                  </div>
                  {canEditar && (
                    <Button onClick={onStartEditTrabajadores} className="laboral-exp-btn-clean">
                      Editar
                    </Button>
                  )}
                </div>

                {trabajadoresList.length > 0 ? (
                  <div className="laboral-exp-rs-list">
                    {trabajadoresList.map((t, idx) => (
                      <div className="laboral-exp-rs-item" key={t.id ?? idx}>
                        <span className="laboral-exp-rs-ordinal">
                          <i className="fa-solid fa-user" />
                        </span>
                        <div className="laboral-exp-rs-info">
                          <div className="laboral-exp-rs-name">
                            {formatNombrePersona(t.nombre_trabajador)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="laboral-exp-field-value">No hay solicitantes registrados</span>
                )}
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ height: "100%" }}>
            {editingRazonesCard ? (
              <>
                <div className="laboral-exp-card-header">
                  <div className="laboral-exp-icon-badge">
                    <i className="fa-solid fa-city" />
                  </div>
                  <h3 className="laboral-exp-card-header-title">Razones Sociales</h3>
                </div>

                <div className="laboral-exp-edit-field" style={{ marginBottom: 16 }}>
                  <span className="laboral-exp-edit-label">Razones sociales vinculadas</span>
                  <Select
                    mode="multiple"
                    labelInValue
                    style={{ width: "100%" }}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    placeholder="Selecciona razón social"
                    options={razonesSocialesOptions}
                    value={editingRazonesForm.razones_sociales_sel || []}
                    onChange={(v) => setEditingRazonesForm((f) => ({ ...f, razones_sociales_sel: v }))}
                  />
                </div>

                <div className="laboral-exp-edit-actions">
                  <Button onClick={onCancelEditRazones} className="laboral-exp-btn-clean">
                    Cancelar
                  </Button>
                  <HappyProvider>
                    <Button
                      type="primary"
                      loading={savingRazonesCard}
                      onClick={onSaveRazones}
                      className="laboral-exp-btn-save"
                      icon={<i className="fa-solid fa-floppy-disk" />}
                    >
                      Guardar
                    </Button>
                  </HappyProvider>
                </div>
              </>
            ) : (
              <>
                <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="laboral-exp-icon-badge">
                      <i className="fa-solid fa-city" />
                    </div>
                    <h3 className="laboral-exp-card-header-title">Razones Sociales</h3>
                  </div>
                  {canEditar && (
                    <Button onClick={onStartEditRazones} className="laboral-exp-btn-clean">
                      Editar
                    </Button>
                  )}
                </div>

                {razonesSociales.length > 0 ? (
                  <div className="laboral-exp-rs-list">
                    {razonesSociales.map((rs, idx) => (
                      <div className="laboral-exp-rs-item" key={rs.id}>
                        <span className="laboral-exp-rs-ordinal">
                          <i className="fa-solid fa-building" />
                        </span>
                        <div className="laboral-exp-rs-info">
                          <div className="laboral-exp-rs-name">{toTitleCase(rs.razon_social)}</div>
                          <div className="laboral-exp-rs-meta">
                            <span><i className="fa-solid fa-envelope" /> {rs.correo || "Sin correo"}</span>
                            <span><i className="fa-solid fa-phone" /> {rs.celular || "Sin teléfono"}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="laboral-exp-field-value">No hay razones sociales vinculadas</span>
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
